import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { BehaviorSubject, EMPTY, Observable, throwError } from 'rxjs';
import { GlobalService } from '../services/global.service';
import { AuthService } from '../services/auth.service';
import { config } from '@configs/config';
import { STORAGE_KEYS } from '@environments/storage.constants';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
	private isRefreshing = false;
	private isLoggingOut = false;

	private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

	constructor(
		private router: Router,
		private globalService: GlobalService,
		private authService: AuthService,
	) {}

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		const originalUrl = req.url;
		let authReq: HttpRequest<any>;

		if (originalUrl.indexOf('/api/') === 0) {
			if (!config.apiUrl) {
				return EMPTY;
			}
			req = req.clone({ url: config.apiUrl + req.url });
		}

		if (this.isAuthRoute(originalUrl)) {
			authReq = req.clone();
		} else if (this.isAuthenticatedApiCall(originalUrl)) {
			const token = GlobalService.getItemFromStorage(STORAGE_KEYS.token);
			authReq = req.clone({
				setHeaders: { Authorization: 'Bearer ' + token }
			});
		} else {
			authReq = req.clone();
		}

		return next.handle(authReq).pipe(
			catchError(err => {
				if (originalUrl.indexOf('/api/') === 0) {
					if (err instanceof HttpErrorResponse && err.status === 401) {
						if (this.isAuthRoute(originalUrl)) {
							this.logoutAndNavigateToLogin();
							return throwError(err.error);
						}

						return this.handle401Error(authReq, next, originalUrl);
					} else if (err.status === 403) {
						this.logoutAndNavigateToLogin();
					} else if (err.status === 404) {
						this.router.navigateByUrl('404');
					}
				}
				return throwError(err.error);
			})
		);
	}

	private isAuthenticatedApiCall(url: string): boolean {
		const user = this.globalService.getUserDetails();
		const token = GlobalService.getItemFromStorage(STORAGE_KEYS.token);

		return !!(user && token && url.indexOf('/api/') === 0);
	}

	private isAuthRoute(url: string): boolean {
		return url.includes('/api/auth/login') || url.includes('/api/auth/refresh') || url.includes('/api/auth/logout');
	}

	private handle401Error(request: HttpRequest<any>, next: HttpHandler, originalUrl: string): Observable<HttpEvent<any>> {
		if (!this.isRefreshing) {
			this.isRefreshing = true;
			this.refreshTokenSubject.next(null);

			return this.authService.refreshToken().pipe(
				switchMap((resp: any) => {
					this.isRefreshing = false;

					const newToken = resp.token;
					this.refreshTokenSubject.next(newToken);

					if (this.isAuthenticatedApiCall(originalUrl)) {
						return next.handle(
							request.clone({
								setHeaders: { Authorization: 'Bearer ' + newToken }
							})
						);
					}

					return next.handle(request);
				}),
				catchError(err => {
					this.isRefreshing = false;
					this.logoutAndNavigateToLogin();
					return throwError(err);
				})
			);
		} else {
			return this.refreshTokenSubject.pipe(
				filter(newToken => newToken !== null),
				take(1),
				switchMap((newToken) => {
					if (this.isAuthenticatedApiCall(originalUrl)) {
						return next.handle(
							request.clone({
								setHeaders: { Authorization: 'Bearer ' + newToken }
							})
						);
					}
					return next.handle(request);
				})
			);
		}
	}

	private logoutAndNavigateToLogin() {
		if (this.isLoggingOut) {
			this.router.navigateByUrl('login');
			return;
		}

		this.isLoggingOut = true;

		this.authService.logout().pipe(take(1)).subscribe({
			next: () => {
				this.router.navigateByUrl('login');
			},
			error: () => {
				this.router.navigateByUrl('login');
			},
		});
	}
}
